import { useState } from "react";
import { Trash2 } from "lucide-react";
import { Switch } from "../ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { ChalkCard } from "./primitives";

export const PERMISSION_LABELS = {
  POST_CHALK_TALK: ["Post in Chalk Talk", "Send announcements to families"],
  MANAGE_VOLUNTEERS: ["Manage volunteers & snacks", "Create duties and snack rotations"],
  MANAGE_ROSTER: ["Manage the roster", "Add, edit, remove or import players and invite parents"],
  POST_EVENTS: ["Add events", "Put practices, games and team events on the calendar"],
  EVENT_CANCEL: ["Cancel or delay events", "Notifies every family on the team"],
  VIEW_CONTACTS: ["See parent emails", "Contact details on the roster"],
  VIEW_MEDICAL: ["See medical notes", "Allergies, conditions, emergency info"],
};

export function StaffMemberRow({ member, keys, canManage, onToggle, onRemove }) {
  const [askMedical, setAskMedical] = useState(false);
  const [askRemove, setAskRemove] = useState(false);
  const name = [member.first_name, member.last_name].filter(Boolean).join(" ") || member.email;
  const id = member.user_id;

  function flip(key, value) {
    if (key === "VIEW_MEDICAL" && value) return setAskMedical(true);
    onToggle(member, key, value, false);
  }

  return (
    <ChalkCard data-testid={`staff-member-${id}`} style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--chalk-board)" }} data-testid={`staff-name-${id}`}>{name}</div>
          <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 2 }}>
            {member.label}{member.is_parent_on_team ? " · also a parent on this team" : ""}
          </div>
        </div>
        {canManage && (
          <button type="button" onClick={() => setAskRemove(true)} data-testid={`staff-remove-${id}`}
            aria-label={`Remove ${name}`}
            style={{ width: 34, height: 34, borderRadius: 8, border: "0.5px solid var(--chalk-border)", background: "#fff", color: "var(--chalk-muted)", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Trash2 size={15} aria-hidden="true" />
          </button>
        )}
      </div>
      {keys.map((k) => (
        <label key={k} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderTop: "0.5px solid var(--chalk-border)" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--chalk-board)" }}>{PERMISSION_LABELS[k]?.[0] || k}</div>
            <div style={{ fontSize: 11.5, color: "var(--chalk-muted)" }}>{PERMISSION_LABELS[k]?.[1]}</div>
          </div>
          <Switch checked={!!member.permissions[k]} disabled={!canManage}
            onCheckedChange={(v) => flip(k, v)} data-testid={`staff-perm-${id}-${k}`}
            className="data-[state=checked]:bg-[var(--green-mid)]" />
        </label>
      ))}
      <AlertDialog open={askMedical} onOpenChange={setAskMedical}>
        <AlertDialogContent data-testid="staff-medical-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Share medical notes with {member.first_name || "this person"}?</AlertDialogTitle>
            <AlertDialogDescription>They'll see every player's allergies, conditions and emergency notes on this team. Only turn this on if they need it to keep kids safe.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="staff-medical-cancel">Keep private</AlertDialogCancel>
            <AlertDialogAction data-testid="staff-medical-confirm" onClick={() => onToggle(member, "VIEW_MEDICAL", true, true)}>Share medical notes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={askRemove} onOpenChange={setAskRemove}>
        <AlertDialogContent data-testid="staff-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {name} from team staff?</AlertDialogTitle>
            <AlertDialogDescription>
              {member.is_parent_on_team ? "They'll keep their parent access to this team." : "They'll lose access to this team."} Duties and posts they created stay with the team.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="staff-remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="staff-remove-confirm" onClick={() => onRemove(member)}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ChalkCard>
  );
}
